import React, { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import './ManualDataEditor.css';

function ManualDataEditor({ report, onSave, onCancel }) {
  const [editedData, setEditedData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Initialize with existing data or empty structure
    setEditedData(report.extractedData || {
      metadata: { reportTitle: '', month: '', year: new Date().getFullYear() },
      financialSummary: { openingBalance: 0, totalReceived: 0, totalDisbursed: 0, closingBalance: 0 },
      projectUpdates: [],
      newProjects: []
    });
  }, [report]);

  const updateFinancial = (field, value) => {
    setEditedData({
      ...editedData,
      financialSummary: {
        ...editedData.financialSummary,
        [field]: parseFloat(value) || 0
      }
    });
  };

  const updateProject = (index, field, value) => {
    const updated = [...editedData.projectUpdates];
    updated[index] = { ...updated[index], [field]: value };
    setEditedData({ ...editedData, projectUpdates: updated });
  };

  const addProject = () => {
    setEditedData({
      ...editedData,
      projectUpdates: [
        ...editedData.projectUpdates,
        {
          projectName: '',
          chapter: '',
          donor: '',
          beneficiaries: 0,
          fundReceived: 0,
          fundDisbursed: 0,
          activities: [],
          status: 'active'
        }
      ]
    });
  };

  const removeProject = (index) => {
    const updated = editedData.projectUpdates.filter((_, i) => i !== index);
    setEditedData({ ...editedData, projectUpdates: updated });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'monthlyReports', report.id), {
        extractedData: editedData,
        status: 'manual_review',
        manuallyEdited: true,
        lastEditedAt: new Date()
      });
      onSave();
    } catch (error) {
      console.error('Error saving:', error);
      alert('Failed to save changes');
    }
    setSaving(false);
  };

  if (!editedData) return <div>Loading...</div>;

  return (
    <div className="manual-editor-modal">
      <div className="editor-container">
        <div className="editor-header">
          <h2>✏️ Edit Report Data</h2>
          <p>Manually review and correct extracted information</p>
        </div>

        {/* Metadata Section */}
        <div className="editor-section">
          <h3>📅 Report Metadata</h3>
          <div className="form-grid">
            <div className="form-field">
              <label>Report Title</label>
              <input
                type="text"
                value={editedData.metadata.reportTitle}
                onChange={(e) => setEditedData({
                  ...editedData,
                  metadata: { ...editedData.metadata, reportTitle: e.target.value }
                })}
                placeholder="e.g., November 2025 Activity Report"
              />
            </div>
            <div className="form-field">
              <label>Month</label>
              <input
                type="month"
                value={editedData.metadata.month}
                onChange={(e) => setEditedData({
                  ...editedData,
                  metadata: { ...editedData.metadata, month: e.target.value }
                })}
              />
            </div>
          </div>
        </div>

        {/* Financial Summary */}
        <div className="editor-section">
          <h3>💰 Financial Summary</h3>
          <div className="form-grid-4">
            <div className="form-field">
              <label>Opening Balance ($)</label>
              <input
                type="number"
                value={editedData.financialSummary.openingBalance}
                onChange={(e) => updateFinancial('openingBalance', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Total Received ($)</label>
              <input
                type="number"
                value={editedData.financialSummary.totalReceived}
                onChange={(e) => updateFinancial('totalReceived', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Total Disbursed ($)</label>
              <input
                type="number"
                value={editedData.financialSummary.totalDisbursed}
                onChange={(e) => updateFinancial('totalDisbursed', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Closing Balance ($)</label>
              <input
                type="number"
                value={editedData.financialSummary.closingBalance}
                onChange={(e) => updateFinancial('closingBalance', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Project Updates */}
        <div className="editor-section">
          <div className="section-header">
            <h3>📊 Project Updates ({editedData.projectUpdates.length})</h3>
            <button onClick={addProject} className="add-btn">+ Add Project</button>
          </div>

          {editedData.projectUpdates.map((project, index) => (
            <div key={index} className="project-editor-card">
              <div className="card-header">
                <h4>Project {index + 1}</h4>
                <button onClick={() => removeProject(index)} className="remove-btn">✕</button>
              </div>

              <div className="form-grid-2">
                <div className="form-field">
                  <label>Project Name *</label>
                  <input
                    type="text"
                    value={project.projectName}
                    onChange={(e) => updateProject(index, 'projectName', e.target.value)}
                    placeholder="Enter project name"
                  />
                </div>
                <div className="form-field">
                  <label>Chapter</label>
                  <input
                    type="text"
                    value={project.chapter || ''}
                    onChange={(e) => updateProject(index, 'chapter', e.target.value)}
                    placeholder="e.g., South Florida"
                  />
                </div>
                <div className="form-field">
                  <label>Donor</label>
                  <input
                    type="text"
                    value={project.donor || ''}
                    onChange={(e) => updateProject(index, 'donor', e.target.value)}
                    placeholder="e.g., Dr. V.G. Dev"
                  />
                </div>
                <div className="form-field">
                  <label>Beneficiaries</label>
                  <input
                    type="number"
                    value={project.beneficiaries}
                    onChange={(e) => updateProject(index, 'beneficiaries', parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className="form-field">
                  <label>Funds Received ($)</label>
                  <input
                    type="number"
                    value={project.fundReceived}
                    onChange={(e) => updateProject(index, 'fundReceived', parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div className="form-field">
                  <label>Funds Disbursed ($)</label>
                  <input
                    type="number"
                    value={project.fundDisbursed}
                    onChange={(e) => updateProject(index, 'fundDisbursed', parseFloat(e.target.value) || 0)}
                  />
                </div>
              </div>
            </div>
          ))}

          {editedData.projectUpdates.length === 0 && (
            <div className="empty-state">
              <p>No projects added yet. Click "+ Add Project" to start.</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="editor-actions">
          <button onClick={onCancel} className="cancel-btn" disabled={saving}>
            Cancel
          </button>
          <button onClick={handleSave} className="save-btn" disabled={saving}>
            {saving ? 'Saving...' : '💾 Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ManualDataEditor;
